import { useUser } from '@clerk/clerk-react'
import { Navigate } from 'react-router-dom'
import { useEffect, useState } from 'react'
import { supabase } from '@/integrations/supabase/client'
import { isAdminAuthenticated } from '@/pages/admin/AdminLogin'

type AdminState = 'loading' | 'authorized' | 'unauthorized'

/**
 * AdminRoute — admin access has two paths:
 *
 *   PATH A — Clerk-bridged user in admin_users DB:
 *     User signs in via Clerk normally, AdminRoute looks up their Supabase
 *     UUID in admin_users. Works for lj@3pr.uk, os@3pr.uk, admin@timeminers.app,
 *     and any future admin added via INSERT INTO admin_users.
 *
 *   PATH B — password-only via /admin/login:
 *     Operator hits /admin/login, posts ADMIN_PASSWORD to verify-admin edge fn,
 *     gets back valid:true. AdminLogin stores 'admin-authenticated' = 'true'
 *     in sessionStorage. AdminRoute now respects that flag (was previously
 *     ignored — caused the lockout LJ flagged on 2026-05-17). Session ends on
 *     tab close or explicit clearAdminSession().
 *
 * Either path grants access. Both paths' authorisation is server-validated
 * (Path A via admin_users RLS query; Path B via verify-admin edge fn).
 */
export function AdminRoute({ children }: { children: React.ReactNode }) {
  const { user, isLoaded } = useUser()
  const [adminState, setAdminState] = useState<AdminState>('loading')

  // Design-preview bypass — see ProtectedRoute for context.
  if (import.meta.env.VITE_PREVIEW_NO_AUTH === "true") {
    return <>{children}</>;
  }

  // PATH B (fast-path): password-only admin session via /admin/login.
  // BUT — the password gate must have ALSO swapped the Clerk session to
  // admin@timeminers.app (via sign_in_token redeem) for supabase-js queries
  // to satisfy admin_select RLS. If the sessionStorage flag is set but no
  // Clerk session exists, every admin page renders empty because the
  // accessToken callback returns null and RLS treats the request as anon.
  // (LJ hit this 2026-05-17: stale sessionStorage from before the ticket
  // flow shipped. Audit doc + Playwright trace both confirmed.)
  const hasPasswordSession = isAdminAuthenticated();
  const clerkRoleIsAdmin = (user?.publicMetadata as any)?.role === 'admin';

  useEffect(() => {
    if (!isLoaded) return

    // Authorize ONLY if the Clerk session is genuinely admin. Either path
    // (Clerk sign-in OR password gate) ends with admin-role Clerk session.
    if (clerkRoleIsAdmin) { setAdminState('authorized'); return; }

    // sessionStorage flag without a Clerk admin session = stale state.
    // Force re-login by clearing the flag and treating as unauthorized.
    if (hasPasswordSession && !user) {
      try { sessionStorage.removeItem('admin-authenticated'); sessionStorage.removeItem('admin-session-token'); } catch {}
      setAdminState('unauthorized')
      return
    }

    if (!user) {
      setAdminState('unauthorized')
      return
    }

    // admin_users.user_id is a uuid (= Supabase uuid set by the Clerk-Supabase bridge),
    // NOT the Clerk text user_xxx. Earlier this used user.id which silently never
    // matched any row, locking out every legit admin.
    const supabaseUuid = user.externalId
    if (!supabaseUuid) {
      setAdminState('loading')
      return
    }

    supabase
      .from('admin_users')
      .select('user_id')
      .eq('user_id', supabaseUuid)
      .maybeSingle()
      .then(({ data, error }) => {
        if (error) {
          console.error('[AdminRoute] DB check failed:', error.message)
          setAdminState('unauthorized')
          return
        }
        setAdminState(data ? 'authorized' : 'unauthorized')
      })
  }, [user, isLoaded, user?.externalId, hasPasswordSession, clerkRoleIsAdmin])

  if (!isLoaded || adminState === 'loading') {
    return (
      <div className="min-h-screen bg-[#0f0f0f] flex items-center justify-center">
        <div className="w-8 h-8 border-2 border-[#C9A96E] border-t-transparent rounded-full animate-spin" />
      </div>
    )
  }

  if (adminState === 'unauthorized') {
    return <Navigate to="/admin/login" replace />
  }

  return <>{children}</>
}

export default AdminRoute
